import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import {
  FileTextIcon,
  TagIcon,
  CompassIcon,
  LightbulbIcon,
  FrameCornersIcon,
  CrosshairIcon,
} from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import type { DocumentInsightAsset, PromptThinkingProcess } from "./types";
import { fadeSlideIn, shimmer, TDot } from "./Autopilot.styled";
import bkgModuleGreenLight from "../../../../../assets/bkg-module-green-light.png";
import { radius } from "@libertify/ui";

// ─── Props ───────────────────────────────────────────────────────────────────

interface PromptInsightsProps {
  documentInsight?: DocumentInsightAsset;
  onComplete?: () => void;
  skipAnimations?: boolean;
}

// ─── Analysis card keys (display order) ──────────────────────────────────────

const ANALYSIS_KEYS: (keyof PromptThinkingProcess)[] = [
  "topic",
  "approach",
  "assumptions",
  "angle",
  "framing",
];

const CARD_ICONS: Record<string, React.ReactNode> = {
  topic: <TagIcon size={18} weight="duotone" color="#247F79" />,
  approach: <CompassIcon size={18} weight="duotone" color="#247F79" />,
  assumptions: <LightbulbIcon size={18} weight="duotone" color="#247F79" />,
  framing: <FrameCornersIcon size={18} weight="duotone" color="#247F79" />,
  angle: <CrosshairIcon size={18} weight="duotone" color="#247F79" />,
};

// ─── Styled components ───────────────────────────────────────────────────────

const Banner = styled.div<{ $bg: string }>`
  background: url(${(p) => p.$bg}) center center / cover no-repeat;
  border-radius: ${radius.box}px;
  padding: 48px 32px;
  margin-bottom: 36px;
  min-width: 0;
  animation: ${fadeSlideIn} 0.4s ease both;

  @media (width <= 768px) {
    padding: 28px 20px;
    border-radius: ${radius.box}px;
    margin-bottom: 24px;
  }
`;

const PromptTag = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #247f79;
  margin-bottom: 10px;
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: 700;
  color: #090e19;
  line-height: 1.3;
  letter-spacing: -0.02em;
  margin: 0 0 24px;

  @media (width <= 768px) {
    font-size: 18px;
    margin: 0 0 16px;
  }
`;

const CardRow = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;

  /* Thin custom scrollbar */
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
`;

const Card = styled.div`
  background: white;
  border-radius: ${radius.box}px;
  padding: 24px;
  min-width: 210px;
  max-width: 240px;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  animation: ${fadeSlideIn} 0.35s ease both;

  @media (width <= 768px) {
    min-width: 175px;
    max-width: 200px;
    padding: 18px;
  }
`;

const CardIcon = styled.div`
  margin-bottom: 10px;
`;

const CardKey = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
  text-transform: capitalize;
`;

const CardDesc = styled.div<{ $truncated: boolean }>`
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.6;
  flex: 1;
  ${(p) =>
    p.$truncated &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

const CardViewMore = styled.span`
  font-size: 12px;
  color: #14b8a6;
  cursor: pointer;
  margin-top: 8px;
  display: inline-block;

  &:hover {
    color: #0d9488;
    text-decoration: underline;
  }
`;

const SkeletonCard = styled.div`
  min-width: 210px;
  max-width: 240px;
  min-height: 148px;
  border-radius: ${radius.box}px;
  flex-shrink: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.6) 25%,
    rgba(255, 255, 255, 0.9) 50%,
    rgba(255, 255, 255, 0.6) 75%
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.6s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (width <= 768px) {
    min-width: 175px;
    max-width: 200px;
  }
`;

const DotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OutlineWrap = styled.div`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid rgba(20, 184, 166, 0.2);
  animation: ${fadeSlideIn} 0.4s ease 0.2s both;
`;

const OutlineHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const OutlinePhrase = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const ScriptBlock = styled.div`
  font-family: "Courier New", Courier, monospace;
  font-size: 12.5px;
  line-height: 1.75;
  color: #374151;
  background: rgba(255, 255, 255, 0.7);
  border-radius: ${radius.box}px;
  padding: 32px;

  @media (width <= 768px) {
    padding: 16px;
    font-size: 12px;
  }
`;

const ChapterTitle = styled.div`
  font-family: "Courier New", Courier, monospace;
  font-size: 11px;
  font-weight: 700;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 14px;
  margin-bottom: 2px;

  &:first-child {
    margin-top: 0;
  }
`;

const ChapterDesc = styled.div`
  font-family: "Courier New", Courier, monospace;
  font-size: 12px;
  color: #164f4b;
  line-height: 1.7;
  padding-left: 16px;
`;

const SeeMore = styled.button`
  margin-top: 12px;
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: #164f4b;
  cursor: pointer;
  font-family: "Courier New", Courier, monospace;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #0d9488;
    text-decoration: underline;
  }
`;

// ─── Constants ───────────────────────────────────────────────────────────────

const TITLE_DELAY_MS = 1000;
const REVEAL_INTERVAL_MS = 600;
const INITIAL_VISIBLE_SECTIONS = 2;
const OUTLINE_DELAY_MS = 800;

// ─── Component ───────────────────────────────────────────────────────────────

export const PromptInsights: React.FC<PromptInsightsProps> = ({
  documentInsight,
  onComplete,
  skipAnimations,
}) => {
  const t = useTranslate();
  const [revealedCount, setRevealedCount] = useState(
    skipAnimations ? ANALYSIS_KEYS.length : 0
  );
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [outlineExpanded, setOutlineExpanded] = useState(false);
  const completedRef = React.useRef(false);
  const cardRowRef = useRef<HTMLDivElement>(null);

  const thinkingProcess = documentInsight?.insight?.thinking_process;
  const outline = documentInsight?.outline;
  const revealStartTimeRef = useRef<number | null>(null);
  const [showCards, setShowCards] = useState(skipAnimations ? true : false);
  const [showOutline, setShowOutline] = useState(skipAnimations ? true : false);

  // When skipping animations, signal completion immediately
  useEffect(() => {
    if (!skipAnimations || completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  }, [skipAnimations, onComplete]);

  // Show the title first, then reveal the card row after a short delay
  useEffect(() => {
    if (!outline) return;
    const timer = setTimeout(() => setShowCards(true), TITLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [outline]);

  // Progressively reveal analysis cards and signal completion
  useEffect(() => {
    if (!thinkingProcess) return;
    if (revealedCount >= ANALYSIS_KEYS.length) {
      // All cards revealed — signal completion if outline is also ready
      if (!completedRef.current && outline) {
        completedRef.current = true;
        onComplete?.();
      }
      return;
    }

    // Track when the first reveal started so we can catch up after tab switch
    if (revealStartTimeRef.current === null && revealedCount === 0) {
      revealStartTimeRef.current = Date.now();
    }

    const timer = setTimeout(() => {
      setRevealedCount((c) => c + 1);
    }, REVEAL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [thinkingProcess, revealedCount, outline, onComplete]);

  // Catch up on card reveals after the tab was in the background
  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        revealStartTimeRef.current !== null &&
        thinkingProcess
      ) {
        const elapsed = Date.now() - revealStartTimeRef.current;
        const expectedCount = Math.min(
          Math.floor(elapsed / REVEAL_INTERVAL_MS) + 1,
          ANALYSIS_KEYS.length
        );
        setRevealedCount((current) => Math.max(current, expectedCount));
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [thinkingProcess]);

  // Show the outline section after a delay once the first card is revealed
  useEffect(() => {
    if (revealedCount < 1 || !outline) return;
    const timer = setTimeout(() => setShowOutline(true), OUTLINE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [revealedCount >= 1, outline]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll the card row so the newly revealed card is visible
  useEffect(() => {
    if (revealedCount === 0) return;
    const el = cardRowRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      // Scroll to the last revealed card (index = revealedCount - 1)
      const cards = el.children;
      const target = cards[revealedCount - 1] as HTMLElement | undefined;
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [revealedCount]);

  const documentTitle = outline?.document_title ?? "";
  const sections = outline?.sections ?? [];
  const previewSections = sections.slice(0, INITIAL_VISIBLE_SECTIONS);
  const hiddenSections = sections.slice(INITIAL_VISIBLE_SECTIONS);

  return (
    <Banner $bg={bkgModuleGreenLight}>
      <PromptTag>{t("media.autopilot.promptInsights.tag")}</PromptTag>
      {documentTitle && <Title>{documentTitle}</Title>}

      {showCards && (
        <CardRow ref={cardRowRef}>
          {ANALYSIS_KEYS.map((key, i) => {
            if (i < revealedCount && thinkingProcess) {
              const isExpanded = expandedIdx === i;
              return (
                <Card key={key}>
                  <CardIcon>{CARD_ICONS[key]}</CardIcon>
                  <CardKey>
                    {t(`media.autopilot.promptInsights.keys.${key}`)}
                  </CardKey>
                  <CardDesc $truncated={!isExpanded}>
                    {thinkingProcess[key]}
                  </CardDesc>
                  {!isExpanded && (
                    <CardViewMore onClick={() => setExpandedIdx(i)}>
                      {t("media.autopilot.viewMore")}
                    </CardViewMore>
                  )}
                </Card>
              );
            }
            return (
              <SkeletonCard key={key}>
                {i === revealedCount && (
                  <DotsRow>
                    <TDot $delay={0} />
                    <TDot $delay={150} />
                    <TDot $delay={300} />
                  </DotsRow>
                )}
              </SkeletonCard>
            );
          })}
        </CardRow>
      )}

      {showCards && outline && showOutline && (
        <OutlineWrap>
          <OutlineHeader>
            <FileTextIcon size={16} weight="duotone" color="#14b8a6" />
            <OutlinePhrase>
              {t("media.autopilot.promptInsights.outlinePhrase")}
            </OutlinePhrase>
          </OutlineHeader>
          <ScriptBlock>
            {previewSections.map((section, i) => (
              <React.Fragment key={i}>
                <ChapterTitle>
                  {String(i + 1).padStart(2, "0")} — {section.title}
                </ChapterTitle>
                <ChapterDesc>{section.description}</ChapterDesc>
              </React.Fragment>
            ))}
            {outlineExpanded &&
              hiddenSections.map((section, i) => (
                <React.Fragment key={i + INITIAL_VISIBLE_SECTIONS}>
                  <ChapterTitle>
                    {String(i + INITIAL_VISIBLE_SECTIONS + 1).padStart(2, "0")}{" "}
                    — {section.title}
                  </ChapterTitle>
                  <ChapterDesc>{section.description}</ChapterDesc>
                </React.Fragment>
              ))}
          </ScriptBlock>
          {!outlineExpanded && hiddenSections.length > 0 && (
            <SeeMore onClick={() => setOutlineExpanded(true)}>
              {t("media.autopilot.promptInsights.seeMoreChapters", {
                count: hiddenSections.length,
              })}
            </SeeMore>
          )}
        </OutlineWrap>
      )}
    </Banner>
  );
};
